import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import axios from 'axios';
import Login from './components/auth/Login/index';
import CustomerDashboard from './components/CustomerDashboard';
import { initCsrfToken, clearCsrfToken, setCsrfToken, fetchCsrfToken } from './utils/csrfUtils';
import './App.css';

// 设置axios默认配置
// 在生产环境中，如果API和前端不在同一域名下，需要设置baseURL
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_API_URL) {
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
} else if (window.location.hostname !== 'localhost') {
  // 如果不是localhost，使用當前協議和域名作為baseURL
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // 如果是HTTPS，不添加端口號；如果是HTTP，使用3000端口
  if (protocol === 'https:') {
    axios.defaults.baseURL = `${protocol}//${hostname}`;
  } else {
    const apiPort = '3000';
    axios.defaults.baseURL = `${protocol}//${hostname}:${apiPort}`;
  }
}

// 确保所有的API请求都使用这个baseURL，不要硬编码
axios.defaults.withCredentials = true;

// 将axios实例暴露给window，以便csrfUtils可以访问
window.axios = axios;

// 測試更新的組件
const UpdateTest = () => {
  const [counter, setCounter] = useState(0);
  
  return (
    <div style={{ position: 'fixed', top: 10, right: 10, background: '#f0f8ff', padding: 10, border: '2px solid #4682b4', borderRadius: '8px', zIndex: 1000, boxShadow: '0 0 10px rgba(0,0,0,0.2)' }}>
      <h3 style={{ color: '#4682b4', margin: '0 0 10px 0' }}>更新測試 v2</h3>
      <p>計數器: <strong>{counter}</strong></p>
      <button 
        onClick={() => setCounter(counter + 1)}
        style={{ background: '#4682b4', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
      >
        增加
      </button>
      <p style={{ fontSize: '12px', marginTop: '8px' }}>熱重載功能測試成功！</p>
    </div>
  );
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 使用useCallback包装handleLogout，避免依赖循环
  const handleLogout = useCallback(async () => {
    try {
      // 调用登出API
      if (token) {
        // 修正登出API路徑
        await axios.post('/api/auth/logout');
      }
    } catch (error) {
      console.error('登出錯誤:', error);
    } finally {
      // 清除本地存储和状态
      localStorage.removeItem('token');
      clearCsrfToken(); // 使用工具函数清除CSRF令牌
      setToken(null);
      setUser(null);

      // 清除axios头部
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // 设置axios拦截器，处理令牌过期
  useEffect(() => {
    // 初始化CSRF令牌
    initCsrfToken(axios);
    // console.log('App初始化時的CSRF令牌狀態:', csrfToken ? '已存在' : '不存在');

    // 不主動請求新令牌，除非登入或刷新令牌時自動獲取
    // 請求拦截器會從cookie中獲取最新的令牌

    // 请求拦截器，添加令牌
    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // 從cookie讀取CSRF令牌而不是localStorage
        const csrfToken = document.cookie.match(/csrfToken=([^;]+)/)?.[1];
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
          // console.log('請求攔截器添加CSRF令牌:', csrfToken.substring(0, 10) + '...');
        } else {
          // console.warn('請求攔截器無法添加CSRF令牌: 令牌不存在');
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // 响应拦截器，处理令牌过期
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // 如果是401错误且未尝试过刷新令牌
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // 如果错误消息指示令牌已过期
          if (error.response.data && error.response.data.expired) {
            try {
              // 尝试刷新令牌
              const response = await axios.post('/api/refresh-token');
              const { token, csrfToken } = response.data;

              // 更新令牌
              localStorage.setItem('token', token);
              if (csrfToken) {
                setCsrfToken(csrfToken); // 使用工具函数设置CSRF令牌
              }

              // 更新axios默认头部
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

              // 更新应用状态
              setToken(token);

              // 重试原始请求
              originalRequest.headers.Authorization = `Bearer ${token}`;

              return axios(originalRequest);
            } catch (refreshError) {
              // 刷新令牌失败，需要重新登录
              handleLogout();
              return Promise.reject(refreshError);
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }, [handleLogout]);

  // 验证令牌
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // 修正驗證令牌的 API 路徑
        const response = await axios.get('/api/auth/verify-token');
        if (response.data.valid) {
          setUser(response.data.user);

          // 檢查是否有保養單重定向標記
          const warrantyRedirect = sessionStorage.getItem('warranty_redirect');
          if (warrantyRedirect === 'true') {
            // console.log('檢測到保養單重定向標記，將導航到保養單列表');
            // 清除標記
            sessionStorage.removeItem('warranty_redirect');
            // 延遲執行，確保React已完全加載
            setTimeout(() => {
              window.location.href = '/warranties';
            }, 100);
          }
        } else {
          // 令牌无效，清除
          handleLogout();
        }
      } catch (error) {
        console.error('令牌驗證錯誤:', error);
        // 验证失败，清除令牌
        handleLogout();
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [token, handleLogout]);

  const handleLogin = (newToken, userData) => {
    // console.log('App.js中的handleLogin被調用，token:', newToken ? newToken.substring(0, 10) + '...' : 'null');

    // 先設置用戶數據，避免觸發不必要的驗證
    setUser(userData);

    // 然後設置令牌
    localStorage.setItem('token', newToken);
    setToken(newToken);

    // console.log('App.js中的狀態已更新，準備重定向到首頁');

    // 設置axios默認headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    // console.log('已設置axios默認Authorization header');
  };

  if (isLoading) {
    return <div className="loading-screen">加载中...</div>;
  }

  return (
    <Router>
      <div className="app">
        {/* 始終顯示更新測試組件 */}
        {/* <UpdateTest /> */}
        
        {token ? (
          <>
            {/* 添加URL檢查日誌 */}
            {/* {console.log('App中的當前URL路徑:', window.location.pathname)} */}
            <CustomerDashboard token={token} user={user} onLogout={handleLogout} />
          </>
        ) : (
          <Login onLogin={handleLogin} />
        )}
      </div>
    </Router>
  );
}

export default App;